/* eslint-disable jsx-a11y/label-has-associated-control */

import {
  cutlistConfigs,
  type Features,
} from '@cutr/constants/cutlist-theme.ts';
import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Info } from '@/primitives/Icons';
import { useCurrentFeatures, useTicker } from '@/theme';

import { useSetFeature } from '../../FeatureProvider';
import styles from './DemoFeaturesSidebar.module.css';

const CUTR_DEFAULT_MATERIAL_SANDWICHES =
  cutlistConfigs.CUTR.features.supportedMaterialSandwiches;

const STORAGE_KEY = 'demo-features-overrides';

const readOverrides = (): Partial<Features> => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<Features>) : {};
  } catch {
    return {};
  }
};

const writeOverrides = (overrides: Partial<Features>) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // ignore
  }
};

const clearOverrides = () => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const DemoFeaturesSidebar = () => {
  const ticker = useTicker();
  if (ticker !== 'CUTR') return null;

  return <SidebarInner />;
};

const SidebarInner = () => {
  const features = useCurrentFeatures();
  const setFeature = useSetFeature();
  const [collapsed, setCollapsed] = React.useState(true);
  const { t } = useTranslation(undefined, {
    keyPrefix: 'demoFeaturesSidebar',
  });
  // Hydrate the provider with any overrides persisted for this session.
  // Runs once on mount.
  React.useEffect(() => {
    const overrides = readOverrides();
    (Object.keys(overrides) as (keyof Features)[]).forEach((key) => {
      setFeature(key, overrides[key] as Features[typeof key]);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFeature = <K extends keyof Features>(
    key: K,
    value: Features[K]
  ) => {
    setFeature(key, value);
    const overrides = readOverrides();
    overrides[key] = value;
    writeOverrides(overrides);
  };

  const reset = () => {
    clearOverrides();
    window.location.reload();
  };

  const edgeProcessing: EdgeProcessingMode = features.hasEdgeProfiling
    ? 'edgeProfiling'
    : features.edgebandingUI
      ? 'edgebanding'
      : 'none';

  const setEdgeProcessing = (mode: EdgeProcessingMode) => {
    updateFeature('edgebandingUI', mode === 'edgebanding');
    updateFeature('hasEdgeProfiling', mode === 'edgeProfiling');
  };

  return (
    <aside className={cn(styles.sidebar, { [styles.collapsed]: collapsed })}>
      <button
        type="button"
        className={styles.toggleButton}
        onClick={() => setCollapsed((c) => !c)}
        aria-label={collapsed ? t('openSidebar') : t('closeSidebar')}
      >
        {collapsed ? '›' : '‹'}
      </button>
      <div className={styles.header}>
        <h2>{t('demoFeatures')}</h2>
        <button type="button" className={styles.resetButton} onClick={reset}>
          {t('reset')}
        </button>
      </div>
      <div className={styles.body}>
        <label className={styles.field}>
          <span className={styles.fieldBooleanRow}>
            <input
              type="checkbox"
              checked={features.supportedMaterialSandwiches.length > 1}
              onChange={(e) =>
                updateFeature(
                  'supportedMaterialSandwiches',
                  e.target.checked
                    ? CUTR_DEFAULT_MATERIAL_SANDWICHES
                    : ['single-core']
                )
              }
            />
            <span className={styles.fieldLabel}>
              {t('enableMaterialGluing')}
            </span>
          </span>
        </label>
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {t('labels.edgeProcessing')}
          </span>
          <EnumButtonGroup
            options={EDGE_PROCESSING_OPTIONS}
            value={edgeProcessing}
            onChange={setEdgeProcessing}
            t={t}
          />
        </div>
        {featureFields.map((field) => (
          <FieldRow
            key={field.key}
            field={field}
            value={features[field.key]}
            onChange={(value) => updateFeature(field.key, value)}
            t={t}
          />
        ))}
      </div>
    </aside>
  );
};

type FieldRowProps = {
  field: FeatureField;
  value: Features[keyof Features];
  onChange: (value: Features[keyof Features]) => void;
  t: (key: string) => string;
};

const FieldRow = ({ field, value, onChange, t }: FieldRowProps) => {
  const label = t(`labels.${field.key}`);
  const tooltip = field.tooltip ? t(`tooltips.${field.tooltip}`) : undefined;

  if (field.kind === 'boolean') {
    return (
      <label className={styles.field}>
        <span className={styles.fieldBooleanRow}>
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked as never)}
          />
          <span className={styles.fieldLabel}>{label}</span>
          {tooltip && (
            <span
              className={styles.infoIcon}
              title={tooltip}
              aria-label={tooltip}
            >
              <Icon icon={<Info />} />
            </span>
          )}
        </span>
      </label>
    );
  }

  if (field.kind === 'enum') {
    return (
      <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <EnumButtonGroup
          options={field.options}
          value={value}
          onChange={onChange}
          t={t}
        />
      </div>
    );
  }

  return (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      <div className={styles.readonlyValue}>
        {typeof value === 'string' || typeof value === 'number'
          ? String(value)
          : JSON.stringify(value, null, 2)}
      </div>
    </div>
  );
};

type EnumButtonGroupProps<T> = {
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  t: (key: string) => string;
};

const EnumButtonGroup = <T,>({
  options,
  value,
  onChange,
  t,
}: EnumButtonGroupProps<T>) => (
  <div className={styles.enumGroup} role="tablist">
    {options.map((option) => {
      const isActive = option === value;
      return (
        <button
          key={String(option)}
          type="button"
          role="tab"
          aria-selected={isActive}
          className={cn(styles.enumButton, {
            [styles.enumButtonActive]: isActive,
          })}
          onClick={() => onChange(option)}
        >
          {t(`options.${String(option)}`)}
        </button>
      );
    })}
  </div>
);

type EdgeProcessingMode = 'none' | 'edgebanding' | 'edgeProfiling';
const EDGE_PROCESSING_OPTIONS: readonly EdgeProcessingMode[] = [
  'none',
  'edgebanding',
  'edgeProfiling',
];

type BooleanField = {
  key: keyof Features;
  kind: 'boolean';
  tooltip?: string;
};
type EnumField<K extends keyof Features = keyof Features> = {
  key: K;
  kind: 'enum';
  options: readonly Features[K][];
  tooltip?: string;
};
type OtherField = { key: keyof Features; kind: 'other'; tooltip?: string };

export type FeatureField = BooleanField | EnumField | OtherField;

const MATERIAL_SUPPORT_TOOLTIP = 'materialSupport';

const booleanFields: BooleanField[] = [
  { key: 'showPricing', kind: 'boolean' },
  { key: 'llmFileImport', kind: 'boolean' },
  {
    key: 'hasPaintingOption',
    kind: 'boolean',
    tooltip: MATERIAL_SUPPORT_TOOLTIP,
  },
  { key: 'csvUpload', kind: 'boolean' },
  { key: 'hasPartStrips', kind: 'boolean' },
  { key: 'beamProfiling', kind: 'boolean' },
  { key: 'additionalProcessing', kind: 'boolean' },
  {
    key: 'continuousGrain',
    kind: 'boolean',
    tooltip: MATERIAL_SUPPORT_TOOLTIP,
  },
];

const enumFields: EnumField[] = [
  {
    key: 'vatDisplayMode',
    kind: 'enum',
    options: ['exclusive', 'inclusive'],
  },
  {
    key: 'hasCreateLabels',
    kind: 'enum',
    options: ['none', 'part', 'group'],
  },
  {
    key: 'grainDirectionLevel',
    kind: 'enum',
    options: ['part', 'group'],
  },
];

export const featureFields: FeatureField[] = [...booleanFields, ...enumFields];
